import { computed, reactive } from 'vue'

const TAB_KEY = 'offers'

export function useOffers (options) {
  const {
    translate,
    offersTabComponent,
    tabActionsComponent
  } = options || {}

  // Bridge to OffersTab.vue's own internal state: it has no other way to
  // reach this outer tab header (a separate component instance, rendered by
  // TabsLayout above OffersTab.vue itself) to put "add new offer" and the
  // offer's own edit/save/cancel/delete buttons on the same back+title row
  // instead of rendering a second header below it.
  const bridge = reactive({
    hasOffer: false,
    isNew: false,
    offerNumber: '',
    saving: false,
    // Backend-driven per-tab permission - locked by default; only an explicit
    // `meta.can_edit === true` from the API unlocks the tab.
    canEdit: false,
    onAddNew: () => {},
    onEdit: () => {},
    onSave: () => {},
    onCancel: () => {},
    onDelete: () => {}
  })

  const tabActions = computed(() => ({
    component: tabActionsComponent,
    title: !bridge.hasOffer
      ? translate('road-maintenance/temporary_regulation/requests.offer')
      : (bridge.isNew
          ? translate('road-maintenance/temporary_regulation/requests.new_offer')
          : translate('road-maintenance/temporary_regulation/requests.offer') + (bridge.offerNumber ? ` ${bridge.offerNumber}` : '')),
    showEditButtons: bridge.hasOffer,
    showEditBtn: bridge.canEdit,
    disableSave: bridge.saving,
    showBackButton: { uiViewName: 'TemporaryRegulationsRequests' },
    visible: true,
    additional_buttons: [
      {
        type: 'new',
        label: translate('road-maintenance/temporary_regulation/requests.add_new_offer'),
        visible: !bridge.hasOffer && bridge.canEdit,
        click: () => bridge.onAddNew()
      },
      {
        type: 'cancel',
        label: translate('road-maintenance/temporary_regulation/requests.delete_offer'),
        prependIcon: 'mdi-delete',
        visible: bridge.hasOffer && !bridge.isNew && bridge.canEdit,
        click: () => bridge.onDelete()
      }
    ],
    onEditClick: () => bridge.onEdit(),
    onSaveClick: () => bridge.onSave(),
    // A custom `onCancelTabSave` skips TabActionButtons' own automatic
    // "set edit off" - call the provided helper ourselves once our own
    // cancel logic has run, so the outer row drops back out of edit mode.
    onCancelTabSave: ({ cancelTabSave }) => {
      bridge.onCancel()
      cancelTabSave()
    }
  }))

  const offersBlock = computed(() => ({
    component: offersTabComponent,
    visible: true,
    bridge
  }))

  return computed(() => ({
    title: translate('road-maintenance/temporary_regulation/requests.offer'),
    key: TAB_KEY,
    tabContent: [
      tabActions.value,
      offersBlock.value
    ],
    // Content.vue calls this once it resets its own shared `edit` flag after
    // the user confirms navigating away with unsaved changes - OffersTab.vue's
    // own edit state lives on `bridge`, entirely outside that shared `edit`
    // prop, so without this its fields would stay unlocked even though the
    // outer header already reset itself.
    afterCancel: () => bridge.onCancel()
  }))
}
